import { z } from "zod";
import { limits } from "./limits.js";

export const transcriptSegmentSchema = z
  .object({
    start: z.number().nonnegative(),
    end: z.number().nonnegative(),
    text: z.string().min(1).max(5000),
    confidence: z.number().min(0).max(1).optional(),
    words: z
      .array(
        z.object({
          start: z.number().nonnegative(),
          end: z.number().nonnegative(),
          text: z.string().min(1).max(120),
        }),
      )
      .max(200)
      .optional(),
  })
  .refine((s) => s.end > s.start, "segment must end after it starts");

export const transcriptSchema = z.object({
  provider: z.string().max(80),
  language: z.string().max(16).optional(),
  text: z.string().max(limits.maxTranscriptLength),
  segments: z.array(transcriptSegmentSchema).max(limits.maxCaptions),
});

/**
 * A single scene of the AI-written narration. `sourceStart`/`sourceEnd` tie it
 * back to the recording so voice can be aligned with what is on screen.
 */
export const generatedSceneSchema = z
  .object({
    id: z.string().min(1).max(64),
    script: z.string().min(1).max(4000),
    sourceStart: z.number().nonnegative(),
    sourceEnd: z.number().nonnegative(),
  })
  .refine((s) => s.sourceEnd > s.sourceStart, "scene must end after it starts");

/**
 * Words for the HEAD and END cards.
 *
 * Optional throughout, and deliberately so: a recording with no speech and
 * little on screen should produce a card the user finishes rather than a
 * confident sentence about nothing. An empty card with a cursor in it is a
 * better prompt than a wrong one.
 */
export const generatedCardsSchema = z.object({
  headTitle: z.string().max(160).optional(),
  headSubtitle: z.string().max(300).optional(),
  endTitle: z.string().max(160).optional(),
});

export const generatedScriptSchema = z.object({
  scenes: z.array(generatedSceneSchema).min(1).max(limits.maxScenes),
  cards: generatedCardsSchema.optional(),
  /**
   * What the call cost, when the provider says. Optional because a mock has no
   * bill and inventing one would put fiction in the ledger.
   */
  usage: z
    .object({
      model: z.string().max(80).optional(),
      inputTokens: z.number().int().nonnegative().optional(),
      outputTokens: z.number().int().nonnegative().optional(),
    })
    .optional(),
});

export type GeneratedCards = z.infer<typeof generatedCardsSchema>;
export type TranscriptSegment = z.infer<typeof transcriptSegmentSchema>;
export type Transcript = z.infer<typeof transcriptSchema>;
export type GeneratedScene = z.infer<typeof generatedSceneSchema>;
export type GeneratedScript = z.infer<typeof generatedScriptSchema>;

/**
 * How much of a recording was actually spoken over.
 *
 * A transcriber handed silence does not return nothing — it returns confident
 * nonsense. On a real 6:33 screen recording with no commentary, Whisper
 * produced eleven segments of Chinese, the same two characters seven times
 * over: ten seconds of "speech" scattered across the whole take.
 *
 * `segments.length > 0` therefore does not mean anybody spoke, and every step
 * that branched on it took the wrong path — the script was written from
 * "rewrite this transcript" rather than "write from what is on screen", and the
 * cut treated 383 of 393 seconds as removable silence.
 *
 * Two signals, because either alone is wrong. Coverage catches the scattered
 * fragments; repetition catches the case where a transcriber loops one phrase
 * densely enough to look like continuous speech.
 */
export interface SpeechReport {
  /** Seconds of the recording inside a transcript segment. */
  spokenSeconds: number;
  /** That, as a share of the recording. */
  coverage: number;
  /** The share of segments whose text is a duplicate of another segment's. */
  repeatedFraction: number;
  segments: number;
  verdict: "spoken" | "silent";
}

/**
 * Below this share of the recording, what came back is noise rather than
 * narration. A person demonstrating something talks over most of it; 2.6% is
 * what a transcriber does with an empty track.
 */
const MIN_SPEECH_COVERAGE = 0.12;

/** Above this share of duplicated lines, the transcriber is looping, not hearing. */
const MAX_REPEATED_FRACTION = 0.5;

/** Only start/end/text are read, so a database row is as good as a parsed one. */
export type SpeechSegment = { start: number; end: number; text: string };

export function speechReport(segments: readonly SpeechSegment[], durationSeconds: number): SpeechReport {
  const spokenSeconds = segments.reduce((total, segment) => total + Math.max(0, segment.end - segment.start), 0);
  const coverage = durationSeconds > 0 ? spokenSeconds / durationSeconds : 0;

  const seen = new Map<string, number>();
  for (const segment of segments) {
    const key = segment.text.trim().toLowerCase();
    if (key) seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  const duplicates = [...seen.values()].reduce((total, count) => total + (count > 1 ? count : 0), 0);
  const repeatedFraction = segments.length > 0 ? duplicates / segments.length : 0;

  const looping = repeatedFraction > MAX_REPEATED_FRACTION;

  // Coverage is a share of the recording, so it means nothing without one.
  // Callers that cannot say how long the video is get the old behaviour —
  // segments mean speech — minus the looping check, which needs no duration.
  // Tightening on a number we do not have would be a different guess, not a
  // better one.
  const enough = durationSeconds > 0 ? coverage >= MIN_SPEECH_COVERAGE : true;

  return {
    spokenSeconds: Number(spokenSeconds.toFixed(2)),
    coverage: Number(coverage.toFixed(4)),
    repeatedFraction: Number(repeatedFraction.toFixed(3)),
    segments: segments.length,
    verdict: segments.length > 0 && enough && !looping ? "spoken" : "silent",
  };
}

/** Whether there is enough real speech to write from, or to cut against. */
export function hasUsableSpeech(segments: readonly SpeechSegment[], durationSeconds: number): boolean {
  return speechReport(segments, durationSeconds).verdict === "spoken";
}
